"use client";

/**
 * NicheV3Root — client wrapper providing a single LayoutGroup + AnimatePresence
 * boundary for the Parallel Routes split at `/niche/[id]`.
 *
 * Framer Motion's SharedLayout transitions (layoutId) only bridge elements that
 * share a **single** LayoutGroup context and that animate through the same
 * presence boundary. Next.js Parallel Routes render sibling slots (`@grid`,
 * `@detail`) concurrently — wrapping both under LayoutGroup and crossfading
 * them with AnimatePresence gives the foil badge / name / hook a real
 * morph-from-A-to-B transition instead of an independent entrance.
 *
 * Boundary identity is keyed by the Detail path segment:
 *   - key="grid"               ← URL is `/niche/[id]` (no candidate)
 *   - key=`<candidateId>`      ← URL is `/niche/[id]/c/[candidateId]`
 *
 * We read the candidate from `useParams()` (path segment) rather than
 * `useSearchParams()` — the old `?c=<candidateId>` query-string design was
 * migrated to the RESTful path segment for share-safe URLs, SEO-friendly
 * canonicals, and cleaner browser history.
 *
 * AnimatePresence then exits the old children while mounting the new children;
 * during the overlap, Framer matches layoutId pairs across the boundary and
 * animates their bounding rects.
 *
 * No-animation edge cases handled by Framer itself:
 *   - Fresh mount (hard reload on `/c/<id>`) → no origin rect → element just
 *     appears at rest. OK.
 *   - Back/forward navigation → segment flips → transition reverses.
 *   - LayoutGroup runtime error → motion falls back to static rendering.
 */

import { LayoutGroup, AnimatePresence } from "framer-motion";
import { useParams } from "next/navigation";

interface LayoutGroupRootProps {
  children: React.ReactNode;
}

export function LayoutGroupRoot({ children }: LayoutGroupRootProps) {
  // `useParams()` surfaces every matched dynamic segment, including the
  // optional `candidateId` from `/niche/[id]/c/[candidateId]`. When we're on
  // the root grid URL, `candidateId` is simply absent from the params object.
  const params = useParams<{ id: string; candidateId?: string }>();
  const candidateId = params?.candidateId ?? null;
  const boundaryKey = candidateId ?? "grid";

  return (
    <LayoutGroup id="niche-v3">
      <AnimatePresence mode="wait" initial={false}>
        {/* key MUST change between grid & detail so AnimatePresence triggers
         * exit/enter. All layoutId elements below this boundary get matched
         * across the crossfade by LayoutGroup. */}
        <div key={boundaryKey} className="contents">
          {children}
        </div>
      </AnimatePresence>
    </LayoutGroup>
  );
}
